import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { articles } from './posts/articles';
import { CITIES } from './data/cities';
import { BUSINESSES } from './data/businessValuations';

// Couleurs hardcodées pour éviter la dépendance circulaire SharedUI <-> GlobalSearch
// (qui causait un crash "Cannot access COLORS before initialization" en production)
const COLORS = { blue: '#0A1929', green: '#A87A1C', dark: '#0A1929', light: '#F4EFE4', lightGreen: '#F4EFE4' };

// Index de recherche : toutes les pages avec leurs mots-clés
const SEARCH_INDEX = [
  { type: 'page', title: 'Accueil', desc: 'Page principale + simulateur + diagnostic', to: '/', icon: '🏠', keywords: 'accueil home main' },
  { type: 'service', title: 'Valorisation d\'entreprise', desc: '900€ — Rapport 20 pages, 3 méthodes M&A', to: '/valorisation', icon: '💎', keywords: 'valorisation valeur prix DCF multiples ANR cession' },
  { type: 'service', title: 'Prévisionnel financier', desc: '500€ — Format banques françaises', to: '/previsionnel', icon: '📈', keywords: 'previsionnel banque BPI credit pret financement' },
  { type: 'service', title: 'Business Plan', desc: '700€ — Création, levée, reprise', to: '/business-plan', icon: '📋', keywords: 'business plan BP création reprise levée fonds' },
  { type: 'service', title: 'Audit de trésorerie', desc: '350€ — Optimisation cash-flow', to: '/audit-tresorerie', icon: '💰', keywords: 'audit tresorerie BFR DSO cash flow optimisation' },
  { type: 'service', title: 'Dossier cession', desc: '1 200€ — Info Memo + plan reprise', to: '/cession', icon: '🤝', keywords: 'cession vendre vente entreprise repreneur' },
  { type: 'sector', title: 'Restauration / CHR', desc: 'Multiples 2x à 4x EBITDA', to: '/valorisation/restaurant', icon: '🍽️', keywords: 'restaurant restauration CHR brasserie cafe' },
  { type: 'sector', title: 'BTP / Artisanat', desc: 'Multiples 2x à 5x EBITDA', to: '/valorisation/btp', icon: '🏗️', keywords: 'BTP batiment travaux artisan construction' },
  { type: 'sector', title: 'Tech / SaaS', desc: 'Multiples 8x à 14x ARR', to: '/valorisation/saas', icon: '💻', keywords: 'tech saas startup ARR MRR logiciel' },
  { type: 'sector', title: 'Commerce / Retail', desc: 'Multiples 3x à 6x EBITDA', to: '/valorisation/commerce', icon: '🛒', keywords: 'commerce boutique magasin retail' },
  { type: 'sector', title: 'Industrie / Manufacture', desc: 'Multiples 4x à 7x EBITDA', to: '/valorisation/industrie', icon: '🏭', keywords: 'industrie usine manufacture production' },
  { type: 'sector', title: 'Santé / Bien-être', desc: 'Multiples 5x à 9x EBITDA', to: '/valorisation/sante', icon: '⚕️', keywords: 'sante medical cabinet dentiste veterinaire' },
  { type: 'sector', title: 'Immobilier', desc: 'Multiples 6x à 10x EBITDA', to: '/valorisation/immobilier', icon: '🏘️', keywords: 'immobilier agence fonciere SCI' },
  { type: 'tool', title: 'Calculateur EBITDA', desc: '🆓 Marge opérationnelle en 30s', to: '/outils/calcul-ebitda', icon: '📊', keywords: 'calculateur calcul EBITDA marge ebit' },
  { type: 'tool', title: 'Calculateur BFR', desc: '🆓 Besoin en fonds de roulement', to: '/outils/calcul-bfr', icon: '💰', keywords: 'BFR fonds roulement DSO DPO calcul' },
  { type: 'tool', title: 'Calculateur DSCR', desc: '🆓 Capacité de remboursement bancaire', to: '/outils/calcul-dscr', icon: '🏦', keywords: 'DSCR remboursement banque CAF annuité' },
  { type: 'tool', title: 'Calculateur Capacité d\'emprunt', desc: '🆓 Combien votre banque va vous prêter ?', to: '/outils/capacite-emprunt', icon: '💼', keywords: 'capacité emprunt entreprise PME maximum emprunt simulation crédit pro' },
  { type: 'tool', title: 'Simulateur Pacte Dutreil', desc: '🆓 Économie fiscale transmission familiale', to: '/outils/pacte-dutreil', icon: '👨‍👩‍👧', keywords: 'pacte dutreil transmission familiale succession entreprise abattement 75' },
  { type: 'tool', title: 'Calculateur Valeur Fonds de commerce', desc: '🆓 Restaurant, boulangerie, pharmacie...', to: '/outils/fonds-de-commerce', icon: '🏪', keywords: 'fonds commerce valeur restaurant boulangerie pharmacie hotel bar' },
  { type: 'tool', title: 'Quiz "Quel service ?"', desc: '🆓 4 questions → recommandation', to: '/quiz', icon: '🎯', keywords: 'quiz questionnaire recommandation service' },
  { type: 'page', title: 'Tarifs', desc: 'Tous nos services et leurs prix', to: '/tarifs', icon: '💰', keywords: 'tarifs prix cout combien' },
  { type: 'page', title: 'FAQ', desc: '27 questions/réponses', to: '/faq', icon: '❓', keywords: 'FAQ questions reponses aide help' },
  { type: 'page', title: 'Glossaire', desc: '30+ termes financiers', to: '/glossaire', icon: '📖', keywords: 'glossaire definition lexique terme' },
  { type: 'page', title: 'Études de cas', desc: '3 cas pratiques commentés (exemples)', to: '/etudes-de-cas', icon: '📊', keywords: 'cas client temoignage exemple' },
  { type: 'page', title: 'Avis clients', desc: 'Témoignages réels et vérifiés', to: '/avis-clients', icon: '⭐', keywords: 'avis temoignage client review reputation' },
  { type: 'page', title: 'À propos', desc: 'Mission, fondateur, valeurs', to: '/a-propos', icon: '👤', keywords: 'a propos qui sommes nous fondateur' },
  { type: 'page', title: 'Contact', desc: 'Nous écrire en 5 minutes', to: '/contact', icon: '📧', keywords: 'contact ecrire email mail' },
  { type: 'page', title: 'Plan du site', desc: 'Toutes les pages classées', to: '/sitemap', icon: '🗺️', keywords: 'sitemap plan site navigation' },
  { type: 'page', title: 'Blog', desc: '10 articles guides PME', to: '/blog', icon: '📚', keywords: 'blog articles guide actualite' },
  // Articles
  ...articles.map(a => ({
    type: 'article', title: a.title, desc: a.category + ' · ' + a.readTime,
    to: `/blog/${a.slug}`, icon: a.cover.emoji, keywords: a.keywords + ' ' + a.title,
  })),
  // Pages ville (programmatic SEO local)
  ...CITIES.map(c => ({
    type: 'city', title: `Valorisation à ${c.name}`, desc: `${c.region} · ${c.pmeCount}`,
    to: `/valorisation/ville/${c.slug}`, icon: '📍',
    keywords: `${c.name} ${c.region} valorisation entreprise locale`,
  })),
  // Guides "Combien vaut [secteur]"
  { type: 'guide', title: 'Combien vaut [secteur] ?', desc: '20 guides de valorisation par activité', to: '/combien-vaut', icon: '📚', keywords: 'combien vaut secteur guide valorisation' },
  ...BUSINESSES.map(b => ({
    type: 'guide', title: `Combien vaut ${b.article} ${b.name} ?`, desc: `Multiples CA ${b.multiplesCa.min}–${b.multiplesCa.max}× · EBE ${b.multiplesEbe.min}–${b.multiplesEbe.max}×`,
    to: `/combien-vaut/${b.slug}`, icon: b.emoji,
    keywords: `combien vaut ${b.name} valeur estimer vente cession ${b.nameLong}`,
  })),
];

const TYPE_LABELS = {
  page: 'Page', service: 'Service', sector: 'Secteur', tool: 'Outil gratuit', article: 'Article', city: 'Ville', guide: 'Guide',
};
const TYPE_COLORS = {
  page: COLORS.blue, service: '#0A1929', sector: '#A87A1C', tool: COLORS.green, article: COLORS.dark, city: '#0A1929', guide: '#0EA5E9',
};

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Open with Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    if (!open) { setQuery(''); setSelectedIdx(0); }
  }, [open]);

  // Search filtering
  const results = query.trim() === '' ? SEARCH_INDEX.slice(0, 8) : SEARCH_INDEX.filter(item => {
    const q = query.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const haystack = (item.title + ' ' + item.desc + ' ' + item.keywords).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    return haystack.includes(q);
  }).slice(0, 12);

  // Keyboard nav within results
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && results[selectedIdx]) {
        e.preventDefault();
        navigate(results[selectedIdx].to);
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, results, selectedIdx, navigate]);

  return (
    <>
      {/* Bouton Search dans la nav (visible sur desktop) */}
      <button onClick={() => setOpen(true)} aria-label="Rechercher (Ctrl+K)" className="search-trigger" style={{
        display: 'none',
        alignItems: 'center', gap: '8px', padding: '6px 12px',
        background: '#F4EFE4', border: '1px solid #D8D3C5', borderRadius: '8px',
        cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', color: '#666',
      }}>
        <span aria-hidden="true">🔍</span>
        <span>Rechercher</span>
        <kbd style={{ padding: '1px 6px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', fontSize: '10px', fontFamily: 'monospace', color: '#888' }}>Ctrl K</kbd>
      </button>
      <style>{`@media (min-width: 768px) { .search-trigger { display: inline-flex !important; } }`}</style>

      {/* Modal de recherche */}
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(10,25,41,0.6)', backdropFilter: 'blur(4px)',
          zIndex: 9995, padding: '10vh 20px 20px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            maxWidth: '600px', margin: '0 auto', background: 'white',
            borderRadius: '16px', boxShadow: '0 25px 60px rgba(10,25,41,0.3)',
            overflow: 'hidden', animation: 'fadeUp 0.2s ease-out',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid #D8D3C5' }}>
              <span style={{ fontSize: '20px' }}>🔍</span>
              <input ref={inputRef} value={query} onChange={e => { setQuery(e.target.value); setSelectedIdx(0); }}
                placeholder="Rechercher une page, un service, un article..."
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: '16px', fontFamily: 'inherit', background: 'transparent' }} />
              <kbd style={{ padding: '2px 8px', background: '#F4EFE4', border: '1px solid #ddd', borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace', color: '#888' }}>ESC</kbd>
            </div>

            <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '8px' }}>
              {results.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
                  Aucun résultat pour "<strong>{query}</strong>"
                </div>
              ) : results.map((item, i) => (
                <button key={item.to + i} onClick={() => { navigate(item.to); setOpen(false); }}
                  onMouseEnter={() => setSelectedIdx(i)}
                  style={{
                    display: 'flex', width: '100%', alignItems: 'center', gap: '12px', padding: '10px 12px',
                    background: selectedIdx === i ? COLORS.light : 'transparent',
                    border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
                    textAlign: 'left', marginBottom: '2px', transition: 'background 0.1s',
                  }}>
                  <span style={{ fontSize: '24px', flexShrink: 0 }}>{item.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#0a1929', marginBottom: '2px' }}>{item.title}</div>
                    <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.desc}</div>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: TYPE_COLORS[item.type], background: TYPE_COLORS[item.type] + '15', padding: '2px 8px', borderRadius: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>
                    {TYPE_LABELS[item.type]}
                  </span>
                </button>
              ))}
            </div>

            <div style={{ padding: '8px 16px', borderTop: '1px solid #D8D3C5', fontSize: '11px', color: '#888', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span><kbd style={{ padding: '1px 5px', background: '#F4EFE4', border: '1px solid #ddd', borderRadius: '3px', fontFamily: 'monospace', fontSize: '10px' }}>↑</kbd> <kbd style={{ padding: '1px 5px', background: '#F4EFE4', border: '1px solid #ddd', borderRadius: '3px', fontFamily: 'monospace', fontSize: '10px' }}>↓</kbd> Naviguer</span>
              <span><kbd style={{ padding: '1px 5px', background: '#F4EFE4', border: '1px solid #ddd', borderRadius: '3px', fontFamily: 'monospace', fontSize: '10px' }}>↵</kbd> Ouvrir</span>
              <span><kbd style={{ padding: '1px 5px', background: '#F4EFE4', border: '1px solid #ddd', borderRadius: '3px', fontFamily: 'monospace', fontSize: '10px' }}>ESC</kbd> Fermer</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
